import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Oval } from 'react-loader-spinner';
import { useTranslation } from 'react-i18next';
import { VERIFYUSER, LOGIN, RESENDOTP, LOGINUSER, BMICALCULATOR, HOME } from '../helpers/Constant';
import { postData } from '../api/api';
import { setSession, setUserType } from '../utils/common';

function VerifyOtp() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [otpError, setOtpError] = useState('');
  const [resendDisabled, setResendDisabled] = useState(false);
  const [resendTimer, setResendTimer] = useState(30);
  const [userData, setUserData] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const otpRefs = [
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null)
  ];

  useEffect(() => {
    if (location.state && location.state.userData) {
      
      setUserData(location.state.userData);
      setEmail(location.state.email || '');
      setPassword(location.state.password || '');
      startResendTimer();
    } else {
      navigate('/register');
    }
  }, [location, navigate]);

  const completeLogin = (sessionData) => {
    setSession(sessionData);
    setUserType("real_user");
    navigate(
      (!sessionData.isTrialComplete && sessionData.trialCompleteAt === null)
        ? `/${BMICALCULATOR}`
        : `/${HOME}`,
      { replace: true }
    );
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value) || value.length > 1) return;
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setOtpError('');
    
    if (value && index < 5) {
      otpRefs[index + 1].current.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs[index - 1].current.focus();
    }
  };

  const handleVerifyOtp = async () => {
    const otpValue = otp.join('');
    
    if (otpValue.length !== 6) {
      setOtpError(t('Please enter a 6-digit OTP'));
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await postData(VERIFYUSER, {
        user: userData._id,
        otp: Number(otpValue)
      });
      if (response.status === 200) {
        if (response.data?.token) {
          completeLogin(response.data);
          return;
        }
        if (email && password) {
          const loginResponse = await postData(LOGINUSER, {
            email: email.trim(),
            password: password.trim()
          });

          if (loginResponse.status === 200) {
            completeLogin(loginResponse.data);
            return;
          }

          setOtpError(loginResponse.message || t('Login failed. Please try again.'));
        } else {
          navigate(`/${LOGIN}`, { replace: true });
        }
      } else {
        setOtpError(response.message || t('Invalid OTP. Please try again.'));
      }
    } catch (error) {
      setOtpError(error.response?.data?.message || t('Verification failed. Please try again.'));
    }
    
    setLoading(false);
  };

  const handleResendOtp = async () => {
    setResendDisabled(true);
    setLoading(true);
    
    try {
      const response = await postData(RESENDOTP, { 
        user: userData._id,
      });
      
      setOtp(['', '', '', '', '', '']);
      setOtpError('');
      if (otpRefs[0].current) otpRefs[0].current.focus();
      
      startResendTimer();
        if (response.status === 200) {
        const tempMessage = t('New OTP sent to your email');
        setSuccessMessage(tempMessage);
        
        setTimeout(() => {
          setSuccessMessage(prev => prev === tempMessage ? '' : prev);
        }, 3000);
      } else {
        setFormErrors({ general: response.message || t('Failed to resend OTP') });
      }
    } catch (error) {
      setOtpError(t('Failed to resend OTP. Please try again.'));
    }
    
    setLoading(false);
  };

  const startResendTimer = () => {
    setResendDisabled(true);
    setResendTimer(30);
    
    const timer = setInterval(() => {
      setResendTimer(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          setResendDisabled(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  return (
    <div className='wrapper bg_paper d-flex flex-column h-100'>
      {loading && (
        <div className="loader">
          <Oval color="#daff00" secondaryColor="#ffffff" strokeWidth={3} strokeWidthSecondary={3} height={50} width={50} />
        </div>
      )}

      <div className='log_in_wrapper flex-fill d-flex flex-column'>
        <div className='logo_wrapper flex-fill d-flex justify-content-center align-items-center py-3'>
          <img src='../images/muvelogo.png' alt="MUVE" />
        </div>
        
        <div className='login-inner'>
          <div className='auth_section otp_section register_section bg-primary d-grid gap-3 p-3 rounded-4 w-100 mx-auto text-center'>
            <h3 className='text-white fw-bold'>{t('Verify Your Email')}</h3>
            <p className='text-white'>
              {t('We sent a 6-digit code to')} <strong>{email}</strong>
            </p>
            
            <div className='otp-inputs d-flex justify-content-center mb-4'>
              {otp.map((digit, index) => (
                <input key={index} type="text" maxLength={1} value={digit} ref={otpRefs[index]}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  className='otp-digit mx-1 text-center'
                  autoFocus={index === 0}
                />
              ))}
            </div>
            
            {otpError && <p className='field_error'>{otpError}</p>}
            <button className='text-white w-100 guest_button rounded-3' onClick={handleVerifyOtp} disabled={loading}>
              {t('Verify')}
            </button>

            <div className='resend-otp mt-3 d-flex flex-wrap justify-content-center align-items-center'>
              <p className='text-white mb-0 me-2'>
                {t("Didn't receive the code?")}
              </p>
              <button className='text-warning bg-transparent border-0 p-0' onClick={handleResendOtp} disabled={resendDisabled || loading}>
                {t('Resend OTP')} 
                {resendDisabled && ` (${resendTimer}s)`}
              </button>
            </div>
            <div className='text-center mt-3'>
              <button  className='text-warning bg-transparent border-0' onClick={() => navigate(`/${LOGIN}`)}>
                {t('Back to Login')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VerifyOtp;